import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  Tooltip,
  alpha,
  styled,
} from '@mui/material';
import {
  Chat as ChatIcon,
  Hub as GraphIcon,
  Gavel as DecisionIcon,
  AutoAwesome as SynthesisIcon,
  Timeline as EvolutionIcon,
} from '@mui/icons-material';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';

/**
 * Interface Mode Types
 */
export type InterfaceMode = 'chat' | 'graph' | 'decision' | 'synthesis' | 'evolution';

/**
 * ModeSelector Props
 */
interface ModeSelectorProps {
  /**
   * Size variant
   */
  size?: 'small' | 'medium' | 'large';

  /**
   * Orientation
   */
  orientation?: 'horizontal' | 'vertical';

  /**
   * Show labels alongside icons
   */
  showLabels?: boolean;

  /**
   * Custom callback when mode changes (in addition to navigation)
   */
  onChange?: (mode: InterfaceMode) => void;

  /**
   * Disable mode switching
   */
  disabled?: boolean;
}

/**
 * Styled ToggleButton with enhanced visual feedback
 */
const StyledToggleButton = styled(ToggleButton)(({ theme }) => ({
  fontWeight: 500,
  textTransform: 'none',
  padding: theme.spacing(1, 2),
  borderRadius: theme.spacing(1),
  gap: theme.spacing(1),

  '&.Mui-selected': {
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    color: theme.palette.primary.main,

    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.2),
    },
  },

  '&:not(.Mui-selected)': {
    color: theme.palette.text.secondary,

    '&:hover': {
      backgroundColor: alpha(theme.palette.text.primary, 0.05),
    },
  },

  transition: theme.transitions.create(['background-color', 'color'], {
    duration: theme.transitions.duration.short,
  }),
}));

/**
 * Beta Chip component
 */
const BetaChip = styled(Chip)(({ theme }) => ({
  height: 18,
  fontSize: '0.65rem',
  fontWeight: 600,
  letterSpacing: '0.5px',
  marginLeft: theme.spacing(0.5),
  backgroundColor: alpha(theme.palette.secondary.main, 0.2),
  color: theme.palette.secondary.main,
  border: `1px solid ${alpha(theme.palette.secondary.main, 0.4)}`,
}));

const NewChip = styled(Chip)(({ theme }) => ({
  height: 18,
  fontSize: '0.65rem',
  fontWeight: 600,
  letterSpacing: '0.5px',
  marginLeft: theme.spacing(0.5),
  backgroundColor: alpha('#4DC4D2', 0.2),
  color: '#4DC4D2',
  border: `1px solid ${alpha('#4DC4D2', 0.4)}`,
}));

/**
 * ModeSelector Component
 *
 * Allows users to switch between Chat and Knowledge Graph (BETA) modes.
 * Persists user preference in localStorage and handles routing automatically.
 *
 * Week 5 Feature: Graph View Beta Launch
 */
const ModeSelector: React.FC<ModeSelectorProps> = ({
  size = 'medium',
  orientation = 'horizontal',
  showLabels = true,
  onChange,
  disabled = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const featureFlags = useFeatureFlags();
  const [mode, setMode] = useState<InterfaceMode>('chat');
  const [firstTimeGraphUser, setFirstTimeGraphUser] = useState(false);

  /**
   * Determine current mode from route
   */
  useEffect(() => {
    const currentPath = location.pathname;

    if (currentPath.startsWith('/knowledge-graph')) {
      setMode('graph');
    } else if (currentPath.startsWith('/decision')) {
      setMode('decision');
    } else if (currentPath.startsWith('/synthesis')) {
      setMode('synthesis');
    } else if (currentPath.startsWith('/evolution')) {
      setMode('evolution');
    } else if (currentPath.startsWith('/chat')) {
      setMode('chat');
    }
  }, [location.pathname]);

  /**
   * Check if user has visited graph view before
   */
  useEffect(() => {
    const hasVisitedGraph = localStorage.getItem('has_visited_graph');
    if (!hasVisitedGraph) {
      setFirstTimeGraphUser(true);
    }
  }, []);

  /**
   * Handle mode change
   */
  const handleModeChange = (_event: React.MouseEvent<HTMLElement>, newMode: InterfaceMode | null) => {
    // Prevent deselecting the current mode
    if (newMode === null) {
      return;
    }

    // Mark graph as visited when user first clicks it
    if (newMode === 'graph' && firstTimeGraphUser) {
      localStorage.setItem('has_visited_graph', 'true');
      setFirstTimeGraphUser(false);
    }

    // Update mode state
    setMode(newMode);

    // Persist preference
    localStorage.setItem('preferred_interface_mode', newMode);

    // Call custom onChange callback
    if (onChange) {
      onChange(newMode);
    }

    // Navigate to appropriate route
    if (newMode === 'chat') {
      navigate('/chat');
    } else if (newMode === 'graph') {
      navigate('/knowledge-graph');
    } else if (newMode === 'decision') {
      navigate('/decision');
    } else if (newMode === 'synthesis') {
      navigate('/synthesis');
    } else if (newMode === 'evolution') {
      navigate('/evolution');
    }
  };

  return (
    <ToggleButtonGroup
      value={mode}
      exclusive
      onChange={handleModeChange}
      size={size}
      orientation={orientation}
      disabled={disabled}
      style={{
        backgroundColor: 'rgba(255,255,255,0.6)',
        borderRadius: 8,
        padding: 4,
      }}
    >
      {/* Chat Mode Button */}
      <Tooltip
        title="Standard chat interface for conversations with AI teammates"
        placement="bottom"
        arrow
      >
        <StyledToggleButton value="chat" aria-label="Chat mode">
          <ChatIcon fontSize={size} />
          {showLabels && <span>Chat</span>}
        </StyledToggleButton>
      </Tooltip>

      {/* Graph Mode Button */}
      <Tooltip
        title={
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              Knowledge Graph Visualization (BETA)
            </div>
            <div style={{ fontSize: '0.75rem' }}>
              Explore your conversations, documents, and topics as an interactive graph.
              {firstTimeGraphUser && (
                <div style={{ marginTop: 4, fontStyle: 'italic' }}>
                  Try it out!
                </div>
              )}
            </div>
          </div>
        }
        placement="bottom"
        arrow
      >
        <StyledToggleButton value="graph" aria-label="Knowledge graph mode">
          <GraphIcon fontSize={size} />
          {showLabels && (
            <span style={{ display: 'flex', alignItems: 'center' }}>
              Graph
              <BetaChip label="BETA" size="small" />
            </span>
          )}
        </StyledToggleButton>
      </Tooltip>

      {/* Decision Mode Button */}
      {featureFlags.decisionMode && (
        <Tooltip
          title={
            <div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                Decision Mode (NEW)
              </div>
              <div style={{ fontSize: '0.75rem' }}>
                Analyze a thesis and see categorized evidence: supporting, contradicting, and neutral.
              </div>
            </div>
          }
          placement="bottom"
          arrow
        >
          <StyledToggleButton value="decision" aria-label="Decision mode">
            <DecisionIcon fontSize={size} />
            {showLabels && (
              <span style={{ display: 'flex', alignItems: 'center' }}>
                Decision
                <NewChip label="NEW" size="small" />
              </span>
            )}
          </StyledToggleButton>
        </Tooltip>
      )}

      {/* Synthesis Mode Button */}
      {featureFlags.synthesisMode && (
        <Tooltip
          title={
            <div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                Synthesis Mode (NEW)
              </div>
              <div style={{ fontSize: '0.75rem' }}>
                Organize knowledge into structured insights with AI-powered topic clustering.
              </div>
            </div>
          }
          placement="bottom"
          arrow
        >
          <StyledToggleButton value="synthesis" aria-label="Synthesis mode">
            <SynthesisIcon fontSize={size} />
            {showLabels && (
              <span style={{ display: 'flex', alignItems: 'center' }}>
                Synthesis
                <NewChip label="NEW" size="small" />
              </span>
            )}
          </StyledToggleButton>
        </Tooltip>
      )}

      {/* Evolution Mode Button */}
      {featureFlags.evolutionMode && (
        <Tooltip
          title={
            <div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                Evolution Mode (NEW)
              </div>
              <div style={{ fontSize: '0.75rem' }}>
                Track how your knowledge evolved over time with temporal analysis.
              </div>
            </div>
          }
          placement="bottom"
          arrow
        >
          <StyledToggleButton value="evolution" aria-label="Evolution mode">
            <EvolutionIcon fontSize={size} />
            {showLabels && (
              <span style={{ display: 'flex', alignItems: 'center' }}>
                Evolution
                <NewChip label="NEW" size="small" />
              </span>
            )}
          </StyledToggleButton>
        </Tooltip>
      )}
    </ToggleButtonGroup>
  );
};

export default ModeSelector;
